/**
 * The organization the user is working in.
 *
 * The org can arrive three ways, and they are tried in this order:
 *
 *  1. **Handed over by the host app** — the `org` attribute or
 *     `DexShell.configure({ org })`. Authoritative, because the host is the only
 *     thing that knows its own routing.
 *  2. **`?org=<uuid>&orgName=<name>` in the URL** — the cross-app handoff the
 *     shell itself emits when linking to an app that takes a query param.
 *  3. **Remembered from the last time** (`localStorage.dexcare_org`), so a reload
 *     of a route that carries no org keeps its label.
 *
 * (1) exists because assuming (2) was wrong. Optimize routes on
 * `/tenant/<uuid>` and DMT on `/org/<uuid>` — there is no `?org=` to read, and
 * asking them to add one would break the promise that no app has to change its
 * URL shape to adopt the shell. Without a direct handoff those apps render the
 * generic "DexCare" mark and every outbound link is built with a blank tenant id.
 *
 * A host-supplied org always wins: it is derived from the route the user is on
 * right now, whereas a query param is a one-time handoff that goes stale the
 * moment the host navigates client-side.
 *
 * `name` matters and is not decoration: the label falls back to the id, so an org
 * handed over without a name renders a raw UUID where the org name belongs.
 */

const STORAGE_KEY = "dexcare_org"

export interface Org {
  id: string
  name: string
  /**
   * The organization's website, which the org mark turns into a real logo.
   *
   * Optional because the shell can also read it from `/me/tenants` itself. A host
   * that already has it should still pass it: the platform path costs a token
   * acquisition plus a tenant-list fetch before the logo can even start loading,
   * so handing over what you already know is the difference between a logo on
   * first paint and one that appears a couple of seconds later.
   */
  infoUrl?: string
}

/** What a host may hand us: an id, or an object carrying the name and website too. */
export type OrgInput =
  | string
  | { id?: string | null; name?: string | null; infoUrl?: string | null }

/** Where the current value came from. Exported for diagnostics — an integrator's
 *  first question is "did my org actually land?". */
export type OrgSource = "host" | "url" | "storage"

let current: Org | null = null
let source: OrgSource | null = null

/**
 * Values that mean "I have no org yet", not "the org is this".
 *
 * The string literals are deliberate: a host binding `org={tenantId}` before its
 * tenant has loaded is the normal case, and templating layers stringify the
 * absent value rather than omitting the attribute. Treating `"undefined"` as a
 * tenant id would produce links that look fine and 404.
 */
const isBlank = (v: string): boolean =>
  v === "" || v === "undefined" || v === "null"

function normalize(input: OrgInput): Org | null {
  const raw = typeof input === "string" ? { id: input } : input
  const id = String(raw?.id ?? "").trim()
  if (isBlank(id)) return null
  const obj = raw && typeof raw === "object" ? raw : {}
  const name = String(obj.name ?? "").trim()
  const infoUrl = String(("infoUrl" in obj ? obj.infoUrl : "") ?? "").trim()
  return {
    id,
    name: isBlank(name) ? id : name,
    infoUrl: isBlank(infoUrl) ? undefined : infoUrl,
  }
}

function persist(org: Org | null): void {
  try {
    if (org) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(org))
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* private mode / quota — the in-memory value still works this session */
  }
}

/**
 * The host tells the shell which org it is showing.
 *
 * Call it as often as you like — on boot and on every client-side org change.
 * Idempotent, and it re-renders nothing by itself; `DexShell.configure` and the
 * `org` attribute both drive a re-render around it.
 *
 * `null` is an explicit clear (sign-out). `undefined` and a blank id are *not*:
 * they mean the host does not know yet, and forgetting a good org because a
 * later render passed an empty string would be worse than waiting.
 */
export function setOrg(input: OrgInput | null | undefined): Org | null {
  if (input === null) {
    clearOrg()
    return null
  }
  if (input === undefined) return current

  const next = normalize(input)
  if (!next) {
    // Loud, because silence here is the whole bug: no org means the org mark
    // reads "DexCare" and every cross-app link carries a blank tenant id.
    console.warn(
      "[dex-app-shell] ignoring an org with no id. The org indicator and every " +
        "cross-app link need one — pass org=\"<uuid>\" or DexShell.configure({ org }).",
    )
    return current
  }

  current = next
  source = "host"
  persist(next)
  return current
}

/**
 * Read the org from the URL, else from storage.
 *
 * Always strips the params, even when the host has already handed over an org, so
 * a stale `?org=` cannot linger in links people copy and share.
 */
export function loadOrg(): Org | null {
  const fromUrl = takeOrgFromUrl()

  // The host's own answer outranks a query param it did not write.
  if (source === "host") return current

  if (fromUrl) {
    current = fromUrl
    source = "url"
    persist(fromUrl)
    return current
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const stored = raw ? (JSON.parse(raw) as Org) : null
    current = stored?.id ? { id: stored.id, name: stored.name || stored.id } : null
    source = current ? "storage" : null
  } catch {
    current = null
    source = null
  }
  return current
}

/** Pull `?org=`/`?orgId=`/`?orgName=` out of the URL and remove them from it. */
function takeOrgFromUrl(): Org | null {
  try {
    const q = new URLSearchParams(window.location.search)
    const id = q.get("org") || q.get("orgId")
    const name = q.get("orgName")
    if (!id) return null

    q.delete("org")
    q.delete("orgId")
    q.delete("orgName")
    const qs = q.toString()
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    )

    return normalize({ id, name })
  } catch {
    return null
  }
}

export const getOrg = (): Org | null => current

export const getOrgSource = (): OrgSource | null => source

export function clearOrg(): void {
  current = null
  source = null
  persist(null)
}

/** Display label. Falls back to a neutral platform name, never to a blank. */
export const orgLabel = (): string => current?.name || "DexCare"

/** Two-character mark, matching TenantMark.tsx: initials for a multi-word name,
 *  first two letters for a single word. */
export function orgInitials(): string {
  const words = orgLabel()
    .replace(/[^A-Za-z0-9 ]/g, "")
    .split(/\s+/)
    .filter(Boolean)
  if (!words.length) return "DX"
  const raw = words.length > 1 ? words.slice(0, 2).map((w) => w[0]).join("") : words[0].slice(0, 2)
  return raw.toUpperCase()
}
